/**
 * Die Sprachen des Routings als statische Vorgabe, die Startsprache und der
 * Name des Sprach-Cookies.
 *
 * Eigene Datei ohne Imports, weil zwei sehr verschiedene Leser sie brauchen:
 * `hooks/useLanguage.jsx` (Vorgabe, bis `/api/i18n/locale-config` antwortet)
 * und die Fehlerseiten von Next.js (`components/fehlerseite/`), die gerade
 * dann laufen, wenn weder das Backend noch das Bündel der App erreichbar ist.
 * Sie lesen daraus nur, welches Adress-Präfix welche Sprache ist.
 */

export interface StandardSprache {
  code: string;
  urlPrefix: string;
  nameNative: string;
  nameGerman: string;
  flag: string;
}

export const DEFAULT_LANGUAGES: Record<string, StandardSprache> = {
  de: { code: 'de', urlPrefix: 'deutsch', nameNative: 'Deutsch', nameGerman: 'Deutsch', flag: '🇩🇪' },
  en: { code: 'en', urlPrefix: 'english', nameNative: 'English', nameGerman: 'Englisch', flag: '🇬🇧' },
  fr: { code: 'fr', urlPrefix: 'francais', nameNative: 'Français', nameGerman: 'Französisch', flag: '🇫🇷' },
  it: { code: 'it', urlPrefix: 'italiano', nameNative: 'Italiano', nameGerman: 'Italienisch', flag: '🇮🇹' },
  es: { code: 'es', urlPrefix: 'espanol', nameNative: 'Español', nameGerman: 'Spanisch', flag: '🇪🇸' },
  nl: { code: 'nl', urlPrefix: 'nederlands', nameNative: 'Nederlands', nameGerman: 'Niederländisch', flag: '🇳🇱' },
  pl: { code: 'pl', urlPrefix: 'polski', nameNative: 'Polski', nameGerman: 'Polnisch', flag: '🇵🇱' },
  sv: { code: 'sv', urlPrefix: 'svenska', nameNative: 'Svenska', nameGerman: 'Schwedisch', flag: '🇸🇪' },
  da: { code: 'da', urlPrefix: 'dansk', nameNative: 'Dansk', nameGerman: 'Dänisch', flag: '🇩🇰' },
  no: { code: 'no', urlPrefix: 'norsk', nameNative: 'Norsk', nameGerman: 'Norwegisch', flag: '🇳🇴' },
  pt: { code: 'pt', urlPrefix: 'portugues', nameNative: 'Português', nameGerman: 'Portugiesisch', flag: '🇵🇹' },
};

/**
 * Heimatland je Sprache — wohin eine Sprache führt, wenn das Land des
 * Besuchers sie nicht anbietet. Gegenstück zu `LANGUAGE_DEFAULT_COUNTRY` in
 * backend/app/utils/locale_resolver.py (Wächter: test_mail_sprache_canon.py).
 * Bewusst eine Tabelle und keine Suche „erstes Land mit dieser Vorgabesprache":
 * die träfe je nach Reihenfolge der Länderliste /at für Deutsch, /ca für
 * Englisch und /be für Niederländisch (121apps-foundation b2ef73d).
 *
 * Hier und nicht in `hooks/useLanguage.jsx`, weil auch die Weiterleitung der
 * Startseite auf dem Server (`proxy.ts`) sie braucht.
 */
export const HEIMATLAND: Record<string, string> = {
  de: 'de', en: 'gb', es: 'es', fr: 'fr', it: 'it', nl: 'nl',
  pl: 'pl', sv: 'se', pt: 'pt', no: 'no', da: 'dk',
};

/** Die Sprache, in der die App startet, wenn nichts anderes bekannt ist. */
export const DEFAULT_LANGUAGE = 'de';

/** Das Cookie, in dem die App die gewählte Sprache ablegt. */
export const LANGUAGE_COOKIE = 'paperball_language';

/** Das Cookie, in dem die App das gewählte Land ablegt. */
export const COUNTRY_COOKIE = 'paperball_country';
